// @ts-strict-ignore
/*
 * Env-aware tracking initialization utils
 *
 * export const mixpanel = initMixpanel('MY MIXPANEL PUBLISHABLE TOKEN');
 */

import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {Bucket} from 'common/report/funnels/models/bar-chart/types';
import {getLocation} from 'common/util/url';

import {gdpr} from '../util/gdpr';
import {debug} from './util';
import {isBrowserEnv, isProdEnv, isTestEnv} from './util/env';

export const commonTrackingEventPropertyName = {
  reportName: `tab`,
  dataGroupId: `Data Group Id`,
  segmentByGroupProperty: `Segment by a group property`,
  filterByGroupProperty: `Filter by a group property`,
  usedLookupTableProperties: `Used lookup profile property`,
  usedEventCustomProperty: `Used event custom property`,
  usedUserCustomProperty: `Used user custom property`,
  numCustomBucketGroupBys: `report info: # custom bucket group-bys`,
  numberOfLookupTablePropertyFilters: `report info: # of lookup profile property filters`,
  numberOfLookupTablePropertyGroupBys: `report info: # of lookup profile property group-bys`,
  numberOfLookupTablePropertyHoldPropConstant: `report info: # of lookup profile property hpc`,
  numberOfLookupTablePropertyAggregation: `report info: # of lookup profile property aggregated`,
  numberOfLookupTablePropertyExpandedEvents: `# of lookup profile property for expand events`,
  reportLayout: `reportLayout`,
  prevSortConfig: `prevSortConfig`,
  sortConfig: `sortConfig`,
  isBoardsSidenavCollapsed: `Boards Sidenav Collapsed`,
  qbAlignment: `QB Alignment`,
  qbCollapsed: `QB Collapsed`,
};

export enum QueryTrackingPropName {
  ChartType = `Chart type`,
  ChartVizType = `Chart visualization type`,
}

export enum TrackingUsageContext {
  QueryBuilder = `query builder`,
  CustomPropertiesModal = `custom properties modal`,
  CustomEventsModal = `custom events modal`,
  CohortsModal = `cohorts modal`,
  Report = `report`, // anywhere outside the QB like in the chart header for example
}

/**
 * Add any report-agnostic props for client query tracking here
 *
 * TODO: consolidate this type with the props from commonTrackingEventPropertyName
 * to ensure that each prop has consistent naming conventions.
 * */
export type ClientQueryTrackingProps = {
  [QueryTrackingPropName.ChartType]: string;
  [QueryTrackingPropName.ChartVizType]?: string;

  // any report-specific properties we'd want to track
  [key: string]: any;
};

export function getNumCustomBucketGroupBys(groupBys: Segment[]) {
  const getFirst = (seg: Segment | Segment[]): {customBucket?: Bucket} => Object.values([].concat(seg)[0])[0];
  return (groupBys || []).filter((segment) => getFirst(segment)?.customBucket).length;
}

/**
 * When Mixpanel webpage is loaded, it starts by loading the mixpanel tracking snippet
 * (https://github.com/mixpanel/analytics/blob/a8741618a26c87900999edda05b4e6590688b681/templates/snippets/mixpanel-jslib-snippet-dev.min.js)
 * which is a stubbed version of the mixpanel-js library that’s responsible for asynchronously
 * loading the mixpanel-js library.
 *
 * initMixpanel() returns the global mixpanel to be used by the callsite. If it's called early enough,
 * the returned mixpanel reference will point to the tracking snippet. If this happens, any resulting
 * tracking calls will not be sent. To work around this, return a proxy instead that always
 * forwards track calls to the latest instance of mixpanel.
 *
 * NOTE: The proxy assumes only the listed proxy handlers (i.e. reading but not writing to the
 * mixpanel object) are used by the call-sites.
 * NOTE: This doesn't fix the issue if the call-site caches a reference to a sub-property of
 * the global mixpanel object (e.g. mixpanel.insights) and then later uses that for tracking.
 * However, I don't expect call-sites are doing this right now, and not having to support this
 * simplifies the proxy logic considerably.
 */
export function initMixpanel(token: string, instanceName?: string, config = {}) {
  const mixpanelProxy = new Proxy(mixpanel, {
    get: (_, key) => mixpanel[key],
    has: (_, key) => key in mixpanel,
    ownKeys: () => Object.keys(mixpanel),
  });

  // check if mixpanel instance was previously initialized
  // note: "initialized" in this context means that mixpanel.init() has been called,
  //       it does NOT refer to the initialization process of upgrading a window.mixpanel stub to a fully loaded module
  if (instanceName) {
    if (instanceName in mixpanel) {
      if (!isTestEnv()) {
        debug.warn(`mixpanel.${instanceName} has already been initialized, this initMixpanel() call will be ignored.`);
      }
      // sic; this is not a mistake, initMixpanel() usages always expect it to return the main lib even if a named instance was initialized
      return mixpanelProxy;
    }
  } else if (`people` in mixpanel) {
    // Accept no substitutes, the only reliable way I've found to check if the primary mixpanel instance was previously initialized is to check if `mixpanel.people` is defined.
    // In a production browser environment, window.mixpanel may still be a basic stub created by an inline snippet; all stub `mixpanel.init()` calls
    // will store configs in a private variable which means checking any usual suspects like `mixpanel.config` tells you nothing about prior init() calls.
    // In a dev or node.js environment, importing the "mixpanel-browser" module directly will give you a `mixpanel` object with most methods already defined before any init()
    // which means checking for common method definitions like `mixpanel.track` tells you nothing about prior init() calls.
    // ---
    // TODO: replace this hack (which relies on non-guaranteed implementation details of the mixpanel SDK) with a stable public SDK method/prop that returns init status
    if (!isTestEnv()) {
      debug.warn(`Primary mixpanel instance has already been initialized, this initMixpanel() call will be ignored.`);
    }
    return mixpanelProxy;
  }

  const defaultConfig = {
    batch_requests: true,
    ignore_dnt: true,
    persistence: `cookie`,
  };

  if (!isProdEnv()) {
    token = `9c4e9a6caf9f429a7e3821141fc769b7`; // Project 132990 Mixpanel Dev

    if (isBrowserEnv()) {
      // Use local api when running on a devbox
      // Note: we cannot rely on APP_ENV since a js app can be running on any host
      // (e.g. stand-alone mode)
      const host = getLocation().host;
      const isDev = /([a-zA-Z0-9]+).dev.mixpanel.org/.test(host);
      if (isDev) {
        defaultConfig[`api_host`] = `${getLocation().protocol}//${host}`;
      }

      // Use local app or staging app when running on devbox or staging server
      const isStage = /stage([0-9]+).mixpanel.com/.test(host);
      if (isDev || isStage) {
        defaultConfig[`app_host`] = `${getLocation().protocol}//${host}`;
      }
    } else {
      defaultConfig[`api_host`] = `https://test.com`;
    }
  }

  // GDPR-restrict tracking until it is allowed
  if (!gdpr.allowTracking) {
    defaultConfig[`opt_out_tracking_by_default`] = true;
  }

  mixpanel.init(token, {...defaultConfig, ...config}, instanceName);

  // sic; this is not a mistake, initMixpanel() usages always expect it to return the main lib even if a named instance was initialized
  return mixpanelProxy;
}
